import React, { useRef } from "react";
import {
    motion,
    useReducedMotion,
    useScroll,
    useTransform,
    useSpring,
} from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Smooth, per-element scroll progress.
 *
 * Maps the element's travel through the viewport bottom (when its top is
 * 90% of viewport height) to the lower-middle (when its top is 45% of
 * viewport height) — i.e., the reveal completes well BEFORE the element
 * reaches the top of the screen. Springs the value for cinematic fluidity.
 *
 * Returns a Framer Motion motion value in [0, 1].
 */
export const useElementProgress = (
    ref,
    offset = ["start 0.95", "start 0.45"]
) => {
    const { scrollYProgress } = useScroll({ target: ref, offset });
    return useSpring(scrollYProgress, {
        stiffness: 140,
        damping: 28,
        mass: 0.5,
        restDelta: 0.001,
    });
};

/**
 * FadeUp — settles before reaching top.
 * Internally creates its own ref so each element gets its own scroll progress.
 */
export const FadeUp = ({
    children,
    delay = 0,
    y = 24,
    className = "",
    offset,
    ...rest
}) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const progress = useElementProgress(
        ref,
        offset || ["start 0.92", "start 0.55"]
    );
    const opacity = useTransform(progress, [0, 1], reduce ? [1, 1] : [0, 1]);
    const yPx = useTransform(progress, [0, 1], reduce ? [0, 0] : [y, 0]);

    return (
        <motion.div
            ref={ref}
            style={{ opacity, y: yPx }}
            className={className}
            {...rest}
        >
            {children}
        </motion.div>
    );
};

/**
 * MaskText — line-by-line mask reveal driven by scroll position of THIS
 * element. Each line completes its reveal in its own scroll sub-range so
 * the whole title is fully visible before reaching the top.
 */
export const MaskText = ({
    lines = [],
    className = "",
    as: As = "h2",
    testId,
    offset,
}) => {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const progress = useElementProgress(
        ref,
        offset || ["start 0.92", "start 0.5"]
    );

    return (
        <As ref={ref} className={className} data-testid={testId}>
            {lines.map((line, i) => {
                const startAt = i * 0.18;
                const endAt = Math.min(0.95, startAt + 0.55);
                return (
                    <MaskLine
                        key={`${line}-${i}`}
                        progress={progress}
                        start={startAt}
                        end={endAt}
                        text={line}
                        reduce={reduce}
                    />
                );
            })}
        </As>
    );
};

const MaskLine = ({ progress, start, end, text, reduce }) => {
    const y = useTransform(
        progress,
        [start, end],
        reduce ? ["0%", "0%"] : ["110%", "0%"]
    );
    const opacity = useTransform(
        progress,
        [start, end],
        reduce ? [1, 1] : [0, 1]
    );
    return (
        <span className="mask-reveal-line">
            <motion.span
                style={{ y, opacity }}
                className="mask-reveal-inner will-change-transform"
            >
                {text}
            </motion.span>
        </span>
    );
};

/**
 * Stagger — children animate in based on their own scroll position, with
 * a small extra delay between siblings for rhythm. Settles before reaching
 * the top of the screen.
 */
export const Stagger = ({ children, delayStep = 0.08, className = "" }) => {
    const reduce = useReducedMotion();
    return (
        <div className={className}>
            {React.Children.map(children, (child, i) => (
                <StaggerChild index={i} delayStep={delayStep} reduce={reduce}>
                    {child}
                </StaggerChild>
            ))}
        </div>
    );
};

const StaggerChild = ({ children, index, delayStep, reduce }) => {
    const ref = useRef(null);
    const progress = useElementProgress(ref, ["start 0.95", "start 0.55"]);
    // Bias slightly later for later siblings
    const bias = Math.min(0.25, index * delayStep);
    const opacity = useTransform(
        progress,
        [bias, bias + 0.5],
        reduce ? [1, 1] : [0, 1]
    );
    const y = useTransform(
        progress,
        [bias, bias + 0.5],
        reduce ? [0, 0] : [18, 0]
    );
    return (
        <motion.div ref={ref} style={{ opacity, y }} className="will-change-transform">
            {children}
        </motion.div>
    );
};

export { EASE };
